import { useCallback, useEffect, useState } from 'react'

import { Profile } from '@cash-web/protos/squareup/franklin/ProfileProto.pb'
import { useOptionalAuth, useOptionalCustomerToken } from '@cash-web/shared-auth-data-access-context'
import { useCanTrack, useRegion } from '@cash-web/shared-region-ui-context-main'
import { canTrack, type Region } from '@cash-web/shared-util-can-track'
import {
  logEvent as logCdfEvent,
  logEventImmediately as logCdfEventImmediately,
  type LogEventParams,
} from '@cash-web/shared-util-cdf-analytics'
import { trackRiskSignal } from '@cash-web/shared-util-risk-signals'
import type { Event as CdfEvent } from '@squareup/customer-data-events-web'
import { userJourneyEventMonitors } from '@squareup/customer-data-events-web'

import { useCdfAnalyticsSendGate } from './CdfAnalyticsSendGateProvider'
import { useUserJourneyCdfEventMonitoring } from './useUserJourneyCdfEventMonitoring'

declare const CDF_APPLICATION_NAME: string

function trackRiskSignalOnce({ customerToken }: { customerToken?: string }) {
  const RISK_SIGNAL_TRACKED = 'riskSignalTracked'

  // We leverage session storage to send risk signal tracking once per browser session for authenticated users
  if (customerToken != null && window.sessionStorage.getItem(RISK_SIGNAL_TRACKED) === null) {
    trackRiskSignal()
    window.sessionStorage.setItem(RISK_SIGNAL_TRACKED, 'true')
  }
}

function useResolvedAuth() {
  const auth = useOptionalAuth()

  const [pendingProfile] = useState(() => {
    let resolve: (data: Profile | undefined) => void
    const promise = new Promise<Profile | undefined>(res => {
      resolve = res
    })
    // We can be reasonably sure this is defined since we know the `new Promise` callback runs synchronously.
    // This is essentially equivalent to `Promise.withResolvers()` but lacks necessary browser support at this time.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return { promise, resolve: resolve! }
  })

  useEffect(() => {
    if (!auth?.isLoading) {
      pendingProfile.resolve(auth?.profile)
    }
  }, [pendingProfile, auth])

  return {
    getProfile: useCallback(() => pendingProfile.promise, [pendingProfile]),
  }
}

type LogCdfEvent = (params: LogEventParams) => void | Promise<void>

type LogCashWebCdfEventHookOptions = {
  riskSignalEnabled?: boolean
  waitForProfile?: boolean
  hostName?: string
}

type LogCashWebCdfEventOptions = {
  region: Region | undefined
  hostName?: string
  customerToken?: string
}

function useLogCashWebCdfEventInternal(
  logEvent: LogCdfEvent,
  { riskSignalEnabled, waitForProfile = true, hostName }: LogCashWebCdfEventHookOptions = {}
) {
  const canTrackRegion = useCanTrack()
  const region = useRegion()
  const optionalCustomerToken = useOptionalCustomerToken()
  const { getProfile } = useResolvedAuth()
  const { processCdfEvent } = useUserJourneyCdfEventMonitoring(userJourneyEventMonitors)
  const sendGate = useCdfAnalyticsSendGate()

  if (riskSignalEnabled) {
    trackRiskSignalOnce({ customerToken: optionalCustomerToken })
  }

  return useCallback(
    async (event: CdfEvent) => {
      const applicationName = CDF_APPLICATION_NAME
      if (canTrackRegion) {
        let profile

        if (optionalCustomerToken === undefined) {
          // Waits for the profile to be loaded (when available) before attempting to track event.
          // Otherwise, we run the risk of logging an "authed" event without a customer token.
          profile = waitForProfile ? await getProfile() : undefined
        }

        processCdfEvent(event)

        await logEvent({
          applicationName,
          event,
          region,
          customerToken: optionalCustomerToken ?? profile?.customer_token,
          hostName,
          ...(sendGate ? { sendGate } : {}),
        })
      }
    },
    [
      waitForProfile,
      canTrackRegion,
      region,
      getProfile,
      optionalCustomerToken,
      hostName,
      processCdfEvent,
      logEvent,
      sendGate,
    ]
  )
}

/**
 * Sends events to CDP when allowed with environmental context.
 * The returned logger runs in the background and contains failures.
 *
 * To optionally have `customer_token` included, be sure to render within an `<AuthProvider />`.
 */
export function useLogCashWebCdfEvent(options: LogCashWebCdfEventHookOptions = {}) {
  const log = useLogCashWebCdfEventInternal(logCdfEvent, options)
  return useCallback(
    (event: CdfEvent): void => {
      void log(event).catch(() => undefined)
    },
    [log]
  )
}

/**
 * Like `useLogCashWebCdfEvent`, but the returned logger's Promise resolves after the request completes
 * and rejects if it fails. Use it for events that must be delivered before navigating away.
 */
export function useLogCashWebCdfEventImmediately(options: LogCashWebCdfEventHookOptions = {}) {
  return useLogCashWebCdfEventInternal(logCdfEventImmediately, options)
}

async function logCashWebCdfEventInternal(
  logEvent: LogCdfEvent,
  event: CdfEvent,
  options: LogCashWebCdfEventOptions
): Promise<void> {
  const { hostName, customerToken, region } = options

  const applicationName = CDF_APPLICATION_NAME
  if (!canTrack(region)) {
    return
  }

  await logEvent({
    applicationName,
    event,
    region,
    customerToken,
    hostName,
  })
}

/**
 * Provides a non-hook based way to log a CDF event, while still guarding against logging events from unsupported regions.
 * Logging runs in the background and failures are contained.
 *
 * This is to be used for logging events outside of the React component tree, like in route loaders.
 */
export function logCashWebCdfEvent(event: CdfEvent, options: LogCashWebCdfEventOptions): void {
  void logCashWebCdfEventInternal(logCdfEvent, event, options).catch(() => undefined)
}

/** Like `logCashWebCdfEvent`, but the Promise resolves after the request completes and rejects if it fails. */
export function logCashWebCdfEventImmediately(event: CdfEvent, options: LogCashWebCdfEventOptions): Promise<void> {
  return logCashWebCdfEventInternal(logCdfEventImmediately, event, options)
}
